// src/axiosConfig.js
import axios from 'axios';
import {
  AUTH_CORRELATION,
  getTokenSessionId,
  handleTerminalSessionFailure,
} from './utils/authUtils';
import logger from './utils/logger';
import { observeAvailabilityError } from './utils/serviceAvailabilityEvents';

// Create axios instance  
const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});


const getHeader = (headers, name) => {
  if (typeof headers?.get === 'function') {
    return headers.get(name);
  }

  const key = Object.keys(headers || {}).find(
    candidate => candidate.toLowerCase() === name.toLowerCase()
  );
  return key ? headers[key] : undefined;
};

const removeHeader = (headers, name) => {
  if (typeof headers?.delete === 'function') {
    headers.delete(name);
    return;
  }

  Object.keys(headers || {}).forEach((key) => {
    if (key.toLowerCase() === name.toLowerCase()) {
      delete headers[key];
    }
  });
};

const extractBearer = (authorization) => {
  if (typeof authorization !== 'string') {
    return null;
  }

  const match = authorization.match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
};

instance.interceptors.request.use(
  (config) => {
    config.headers ||= {};

    if (config.skipAuthSessionHandling === true) {
      // Axios merges instance defaults before this interceptor. Remove any
      // inherited bearer here so public credential flows remain isolated from
      // a stale login in another tab.
      removeHeader(config.headers, 'Authorization');
      config.__authSessionBearer = null;
      return config;
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    config.__authSessionBearer = extractBearer(getHeader(config.headers, 'Authorization'));
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    const replacementToken = response.headers?.['x-auth-token'];
    if (replacementToken) {
      const requestToken = response.config?.__authSessionBearer
        || extractBearer(getHeader(response.config?.headers, 'Authorization'));
      const currentToken = localStorage.getItem('token');
      const requestSessionId = getTokenSessionId(requestToken);

      // A successful response authorizes replacement only for the session that
      // made the request. Synchronous comparison/write prevents a late response
      // from session A overwriting a newer session B.
      if (
        requestSessionId
        && requestSessionId === getTokenSessionId(currentToken)
        && requestSessionId === getTokenSessionId(replacementToken)
      ) {
        localStorage.setItem('token', replacementToken);
      }
    }
    return response;
  },
  async (error) => {
    observeAvailabilityError(error, instance.defaults?.baseURL);
    logger.error('Axios response error', {
      status: error.response?.status,
      code: error.response?.data?.code,
      method: error.config?.method,
    });
    
    const requestToken = error.config?.__authSessionBearer
      || extractBearer(getHeader(error.config?.headers, 'Authorization'));
    const status = error.response?.status;
    const code = error.response?.data?.code;
    let correlation = null;

    if (error.config?.skipAuthSessionHandling !== true && requestToken) {
      if (status === 401 && code === 'TOKEN_INVALID') {
        correlation = AUTH_CORRELATION.EXACT_TOKEN;
      } else if (
        (status === 401 && ['TOKEN_INVALID_OR_REVOKED', 'TOKEN_EXPIRED'].includes(code))
        || (status === 403 && code === 'USER_NOT_ACTIVE')
        || (status === 404 && code === 'USER_NOT_FOUND')
      ) {
        correlation = AUTH_CORRELATION.VERIFIED_SESSION;
      }
    }

    if (correlation) {
      await handleTerminalSessionFailure({
        expectedToken: requestToken,
        correlation,
      });
    }

    return Promise.reject(error);
  }
);
export default instance;
